import React, { FC, FormEvent, useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  CloseButton,
  Filters,
  IconCog,
  ILibGroupedOption,
  ILibFilters,
  ILibOption,
  ILibTranslations,
  Refresh,
  Spinner,
} from "@infologistics/frontend-libraries";

import { IEnvelopesHeadProps as IProps } from "@views/envelopes/types";
import { useAppDispatch, useAppSelector } from "@app/hooks/redux";
import {
  applyExtendedDateStringFilterParams,
  fillFilters,
  fillRenderValue,
  getFiltersTranslations,
  getParamsFromFilters,
} from "@app/utils/filters";
import {
  displayBasicNotification,
  displayConfirmNotification,
  displayErrorNotification,
  displaySuccessNotification,
  getInstance,
  getOldInstance,
  getParamsFromSearch,
  getSupportedLocale,
} from "@app/utils/utils";
import { useLocation, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import { IFiltersResult } from "@app/utils/typesFilter";
import { AxiosError } from "axios";
import { closeLoader } from "@app/store/modules/loader";
import {
  DEFAULT_VISIBLE_ENVELOPES_FILTERS,
  envelopesFilters,
  getEnvelopesFilters,
  sortEnvelopeLabels,
} from "./envelopesFilters";
import { QueryLikeFilter } from "@app/common/QueryLikeFilter";
import { setSearchContext } from "@app/store/modules/utils";
import { FilterName } from "@app/const/filters";
import EnvelopesService, {
  EnvelopeExcelExportJobStatus,
  IEnvelopeExcelExportJobPostRequest,
  IEnvelopeExcelExportJobResponse,
} from '@services/envelopes';
import BoxesService from '@services/boxes';
import { Direction, EnvelopeAllovedActions, EnvelopeDirection, Modal } from "@app/const/consts";
import { useSearchParams } from "react-router-dom";
import { ACTION_REQUIRED_FOLDER } from "@hooks/envelopes/useGetParams";
import { IParams } from "@app/types";
import cn from 'classnames';
import {
  getEnvelopeDateOnlyFilterNames,
  getEnvelopeFilterFields,
  getEnvelopeSelectSearchPlaceholder,
} from "../dynamicFields";
import { getEnvelopeCheckboxKey } from "@hooks/envelopes";
import { setModal } from "@store/modules/modal";
import { EnvelopeMassActionModal } from "../EnvelopesTable/EnvelopeMassActionModal";
import { EnvelopeMassDownloadModal } from "../EnvelopesTable/EnvelopeMassDownloadModal";
import { setUserSettings } from "@store/modules/user";
import {
  EnvelopeFieldType,
  IEnvelopeField,
  ILabel,
} from "@store/modules/boxes/types";
import UsersService from "@services/users";
import UserFiltersService from "@services/userFilters";
import { IUserFilter } from "@services/userFilters/types";
import {
  getUserFilterParams,
  getUserFilterRoute,
  normalizeUserFilterParams,
  notifyUserFiltersUpdated,
  USER_FILTER_TITLE_MAX_LENGTH,
} from "@utils/userFilters";
import {
  getBoxFiltersViewSettings,
  getEnvelopeFavoriteViewSettingsNextValue,
  getEnvelopeFavoriteViewSettingsRemovedValue,
  getEnvelopeFiltersViewSettingsNextValue,
  getOrderedEnvelopeFields,
} from "../envelopeViewSettings";
import { setCurrentUserFilterOguid } from '@store/modules/envelopes';
import { DEFAULT_LABEL_COLOR, getReadableTextColor } from '@utils/colors';
import MassiveActionsDropdown from '@common/MassiveActionsDropdown';
import { IMassiveActionDropdownItem } from '@common/MassiveActionsDropdown/types';
import {
  ENVELOPE_MASS_ACTIONS,
  getEnvelopeMassActionAvailability,
} from './massActions';
import {
  getContentDispositionFileName,
  getContentDispositionHeader,
  getEnvelopeExportTargets,
  IEnvelopeExportTarget,
  waitForEnvelopeExcelExportJob,
} from './excelExport';

import styles from './styles.module.scss';

const LS_ENVELOPES_FILTERS_KEY = "envelopes";
const ACTION_REQUIRED_FILTER_NAME = "isActionRequired";
const FILTER_PANEL_OPEN_ANIMATION_MS = 350;
const FILTER_PANEL_CLOSE_ANIMATION_MS = 400;

type FilterAddOption = ILibOption | ILibGroupedOption<ILibOption>;

const StarIcon: FC<{ isActive: boolean }> = ({ isActive }) => (
  <svg
    aria-hidden='true'
    className={cn(styles.favoriteStarIcon, isActive && styles.favoriteStarIconActive)}
    focusable='false'
    height='16'
    viewBox='0 0 16 16'
    width='16'
  >
    <path fillRule="evenodd" clipRule="evenodd" d="M10.247 6.014a1 1 0 01-.751-.545L8.002 2.46 6.529 5.465a1 1 0 01-.755.55L2.417 6.5l2.434 2.38a1 1 0 01.286.889l-.578 3.285 2.97-1.583a1 1 0 01.94 0l2.973 1.584-.574-3.288a1 1 0 01.285-.886l2.432-2.38-3.338-.488zm3.862-.027zm-12.42.62l-.053-.36.053.36zm8.703-1.582L8.57 1.353a.636.636 0 00-1.142.003L5.631 5.025l-4.086.592a.636.636 0 00-.353 1.085l2.96 2.895-.707 4.02a.636.636 0 00.926.672L8 12.355l3.629 1.934a.636.636 0 00.926-.671l-.702-4.021 2.956-2.895a.637.637 0 00-.354-1.085l-4.063-.592z"></path>
  </svg>
);

const getOrderedFilterKeys = (filterKeys: string[], order?: string[]): string[] => {
  const filterKeysSet = new Set(filterKeys);
  const orderedKeys = (order ?? []).filter((key) => filterKeysSet.has(key));
  const orderedKeysSet = new Set(orderedKeys);

  return [
    ...orderedKeys,
    ...filterKeys.filter((key) => !orderedKeysSet.has(key)),
  ];
};

const getOrderedVisibleFilterKeys = (
  filterNames: string[],
  customFilterOrder: string[]
): string[] => {
  const systemFilterOrder = envelopesFilters.order as readonly string[];
  const filterNamesSet = new Set(filterNames);
  const visibleSystemFilters = envelopesFilters.order.filter((filterName) =>
    filterNamesSet.has(filterName)
  );
  const orderedCustomFilters = customFilterOrder.filter((filterName) =>
    filterNamesSet.has(filterName) && !systemFilterOrder.includes(filterName)
  );
  const orderedCustomFiltersSet = new Set(orderedCustomFilters);

  return [
    ...visibleSystemFilters,
    ...orderedCustomFilters,
    ...filterNames.filter((filterName) => (
      !systemFilterOrder.includes(filterName) &&
      !orderedCustomFiltersSet.has(filterName)
    )),
  ];
};

const FILTER_PANEL_PLACEMENT_TARGET_SELECTOR = [
  '.fl-filter .fl-range_select',
  '.fl-filter input[name="suggest-handbook-value"]',
  '.fl-filter input[name="multi-suggest-handbook-value"]',
].join(', ');

enum EnvelopeMassUtilityAction {
  ASSIGN_LABEL = 'ASSIGN_LABEL',
  DOWNLOAD = 'DOWNLOAD',
  DOWNLOAD_REGISTRY = 'DOWNLOAD_REGISTRY',
}

const saveFiltersOrder = (order: string[]): void => {
  localStorage.setItem(LS_ENVELOPES_FILTERS_KEY, JSON.stringify(order));
};

const getMassActionTranslation = (action: EnvelopeAllovedActions): string => {
  switch (action) {
    case EnvelopeAllovedActions.SIGN:
      return 'envelopes:actions:sign';
    case EnvelopeAllovedActions.APPROVE:
      return 'envelopes:actions:approve';
    case EnvelopeAllovedActions.DECLINE_SIGN:
      return 'envelopes:actions:declineSign';
    case EnvelopeAllovedActions.DECLINE_APPROVE:
      return 'envelopes:actions:declineApprove';
    case EnvelopeAllovedActions.STOP_PROCESS:
      return 'envelopes:actions:stopProcess';
    default:
      return 'common:actions';
  }
};

const EnvelopesHead: FC<IProps> = ({
  boxLabels,
  envelopeFields,
  isFieldViewSettingsOpen,
  loadEnvelopes,
  onOpenFieldViewSettings,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(["envelopes", "filters", "common", "envelope", "boxes", "crypto"]);
  const { instance } = useAppSelector((state) => state.utils);
  const userOguid = useAppSelector((state) => state.user.profile.oguid);
  const profileLocale = useAppSelector((state) => state.user.profile.locale);
  const userSettings = useAppSelector((state) => state.user.settings);
  const appState = useAppSelector((state) => state);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { directionOrFolder, boxOguid, directionParam } = useParams();
  const [ searchParams ] = useSearchParams();

  const [isOpenFilters, setIsOpenFilters] = useState(false);
  const [isFiltersCloseVisible, setIsFiltersCloseVisible] = useState(false);
  const [isFiltersPanelTransitionActive, setIsFiltersPanelTransitionActive] = useState(false);
  const [filtersOrder, setFiltersOrder] = useState<string[]>(
    envelopesFilters.order
  );
  const [filters, setFilters] = useState<ILibFilters>(envelopesFilters.data);
  const [queryLikeKey, setQueryLikeKey] = useState(false);
  const [blockRegistryDownload, setBlockRegistryDownload] = useState(false);
  const [selectedMassAction, setSelectedMassAction] = useState<EnvelopeAllovedActions | null>(null);
  const [highlightedFilterName, setHighlightedFilterName] = useState<string | null>(null);
  const [userFilters, setUserFilters] = useState<IUserFilter[]>([]);
  const [massAssignedLabels, setMassAssignedLabels] = useState<ILabel[]>([]);
  const [isMassLabelPickerOpen, setIsMassLabelPickerOpen] = useState(false);
  const [updatingMassLabels, setUpdatingMassLabels] = useState(false);
  const [isFavoritePopoverOpen, setIsFavoritePopoverOpen] = useState(false);
  const [favoriteTitle, setFavoriteTitle] = useState('');
  const [favoriteTitleError, setFavoriteTitleError] = useState('');
  const [isFavoriteSaving, setIsFavoriteSaving] = useState(false);
  const isSubmittingFiltersRef = useRef(false);
  const filtersWrapperRef = useRef<HTMLDivElement>(null);
  const favoriteFilterRef = useRef<HTMLDivElement>(null);

  const personalBoxOguid = useAppSelector((state) => state.user.profile.boxOguid);
  const envelopes = useAppSelector((state) => state.envelopes.envelopes.data);
  const checkedEnvelopes = useAppSelector((state) => state.envelopes.checkedEnvelopes);
  const isAllEnvelopesSelected = useAppSelector((state) => state.envelopes.isAllEnvelopesSelected);
  const currentBoxOguid = boxOguid ?? personalBoxOguid;
  const currentDirection = useMemo((): EnvelopeDirection | null => {
    const routeDirection = directionOrFolder ?? directionParam;

    if (routeDirection === Direction.INCOMING) return EnvelopeDirection.INCOMING;
    if (routeDirection === Direction.OUTGOING) return EnvelopeDirection.OUTGOING;

    return null;
  }, [directionOrFolder, directionParam]);
  const isActionRequiredFolder = directionOrFolder === ACTION_REQUIRED_FOLDER;
  const isRouteHiddenFilter = (filterName: string): boolean => (
    isActionRequiredFolder && filterName === ACTION_REQUIRED_FILTER_NAME
  );
  const favoriteFilterRoute = useMemo(
    () => currentDirection && currentBoxOguid
      ? getUserFilterRoute(currentBoxOguid, currentDirection)
      : '',
    [currentBoxOguid, currentDirection]
  );
  const favoriteFilterParams = useMemo(
    () => getUserFilterParams(getParamsFromSearch(location.search, false)),
    [location.search]
  );
  const currentUserFilter = useMemo(
    () => userFilters.find((filter) => (
      filter.route === favoriteFilterRoute &&
      normalizeUserFilterParams(filter.params) === favoriteFilterParams
    )),
    [favoriteFilterParams, favoriteFilterRoute, userFilters]
  );
  const hasFavoriteFilterParams = Boolean(favoriteFilterRoute && favoriteFilterParams);
  const sortedBoxLabels = useMemo(() => sortEnvelopeLabels(boxLabels), [boxLabels]);
  const filtersConfig = useMemo(
    () => getEnvelopesFilters(envelopeFields, currentDirection, currentBoxOguid, boxLabels),
    [boxLabels, currentBoxOguid, envelopeFields, currentDirection]
  );
  const envelopeFieldsByKey = useMemo(
    () => envelopeFields.reduce<Record<string, IEnvelopeField>>((acc, field) => ({
      ...acc,
      [field.systemKey]: field,
    }), {}),
    [envelopeFields]
  );
  const listFields = useMemo(
    () => getEnvelopeFilterFields(envelopeFields, currentDirection),
    [currentDirection, envelopeFields]
  );
  const savedBoxFiltersViewSettings = useMemo(
    () => getBoxFiltersViewSettings(
      userSettings.envelopeViewSettings,
      currentBoxOguid,
      currentUserFilter?.oguid
    ),
    [currentBoxOguid, currentUserFilter?.oguid, userSettings.envelopeViewSettings]
  );
  const orderedFieldFilterKeys = useMemo(
    () => getOrderedEnvelopeFields(listFields, savedBoxFiltersViewSettings.order)
      .map(({ systemKey }) => systemKey),
    [listFields, savedBoxFiltersViewSettings.order]
  );
  const allFilterKeys = useMemo(
    () => [
      ...envelopesFilters.order,
      ...orderedFieldFilterKeys,
    ].filter((filterName) => (
      filterName in filtersConfig.data &&
      !isRouteHiddenFilter(filterName)
    )),
    [filtersConfig.data, isActionRequiredFolder, orderedFieldFilterKeys]
  );
  const filterVisibilitySettings = useMemo(
    () => {
      const order = [
        ...envelopesFilters.order.filter((filterName) => allFilterKeys.includes(filterName)),
        ...getOrderedFilterKeys(orderedFieldFilterKeys, savedBoxFiltersViewSettings.order),
      ];
      const filterKeysSet = new Set(order);
      const hasSavedSettings = Boolean(
        savedBoxFiltersViewSettings.order || savedBoxFiltersViewSettings.hidden
      );
      const defaultVisibleFilterKeys = new Set([
        ...DEFAULT_VISIBLE_ENVELOPES_FILTERS,
        ...orderedFieldFilterKeys.slice(0, 3),
      ]);
      const hidden = hasSavedSettings
        ? (savedBoxFiltersViewSettings.hidden ?? []).filter((filterName) =>
          filterKeysSet.has(filterName)
        )
        : order.filter((filterName) => !defaultVisibleFilterKeys.has(filterName));

      return {
        hidden,
        order,
      };
    },
    [allFilterKeys, savedBoxFiltersViewSettings]
  );
  const visibleFilterKeys = useMemo(
    () => {
      const hiddenFilterKeys = new Set(filterVisibilitySettings.hidden);

      return filterVisibilitySettings.order.filter((filterName) =>
        !hiddenFilterKeys.has(filterName)
      );
    },
    [filterVisibilitySettings]
  );
  const activeSearchFilterKeys = useMemo(
    () => {
      const filterKeys = new Set(Object.keys(filtersConfig.data));
      const activeKeys = Object.keys(getParamsFromSearch(location.search, false))
        .map((paramName) => ['label.fullKeys', 'label.key'].includes(paramName) ? 'labelKey' : paramName.split('.')[0])
        .filter((filterName) => filterKeys.has(filterName) && !isRouteHiddenFilter(filterName));

      return Array.from(new Set(activeKeys));
    },
    [filtersConfig.data, isActionRequiredFolder, location.search]
  );
  const activeSearchFilterKeysHash = activeSearchFilterKeys.join('|');
  const normalizeFiltersOrder = (
    order: string[] = visibleFilterKeys
  ): string[] => {
    const filterNamesSet = new Set(Object.keys(filtersConfig.data));
    const selectedFilterNames = order.filter((filterName) => (
      filterNamesSet.has(filterName) &&
      !isRouteHiddenFilter(filterName)
    ));

    return getOrderedVisibleFilterKeys(
      selectedFilterNames,
      filterVisibilitySettings.order
    );
  };
  const inactiveFieldFilterOptions = useMemo<FilterAddOption[]>(
    () => {
      const activeFilterNames = new Set(filtersOrder);
      const getFilterOption = (filterName: string): ILibOption => {
        const translation = filtersConfig.data[filterName]?.translation ?? filterName;
        const label = translation.includes(':') ? t(translation) : translation;

        return {
          label,
          value: filterName,
        };
      };
      const systemOptions = envelopesFilters.order
        .filter((filterName) => !activeFilterNames.has(filterName))
        .filter((filterName) => !isRouteHiddenFilter(filterName))
        .filter((filterName) => filterName in filtersConfig.data)
        .map(getFilterOption);
      const customOptions = orderedFieldFilterKeys
        .filter((filterName) => !activeFilterNames.has(filterName))
        .filter((filterName) => !isRouteHiddenFilter(filterName))
        .filter((filterName) => filterName in filtersConfig.data)
        .map(getFilterOption);

      return [
        ...(systemOptions.length
          ? [{
            label: t('filters:systemFields'),
            options: systemOptions,
          }]
          : []),
        ...(customOptions.length
          ? [{
            label: t('filters:customFields'),
            options: customOptions,
          }]
          : []),
      ];
    },
    [filtersConfig.data, filtersOrder, isActionRequiredFolder, orderedFieldFilterKeys, t]
  );
  const filtersTranslations = useMemo<ILibTranslations>(() => {
    const translations = getFiltersTranslations(t);

    return {
      ...translations,
      common: {
        ...translations.common,
        equal: '=',
        notEqual: '!=',
        suggestHandbookText: {
          ...translations.common.suggestHandbookText,
          placeholder: t('envelope:selectSearchPlaceholderFallback'),
        },
      },
      sidebar: {
        ...translations.sidebar,
        addFilter: t('filters:addFilter'),
      },
    };
  }, [t]);
  const selectedEnvelopes = useMemo(
    () => envelopes.filter((envelope) => checkedEnvelopes[getEnvelopeCheckboxKey(envelope)]),
    [checkedEnvelopes, envelopes]
  );
  const selectedEnvelopeLabels = useMemo(() => {
    const labelsByKey = new Map<string, ILabel>();

    selectedEnvelopes.forEach((envelope) => {
      envelope.labels?.forEach((label) => labelsByKey.set(label.fullKey, label));
    });

    return Array.from(labelsByKey.values());
  }, [selectedEnvelopes]);
  const zipExportFilterParams = useMemo<IParams>(() => ({
    ...applyExtendedDateStringFilterParams(searchParams, true),
    ...(isActionRequiredFolder ? { isActionRequired: true } : {}),
  }), [isActionRequiredFolder, searchParams]);
  const massActionAvailability = useMemo(
    () => getEnvelopeMassActionAvailability(selectedEnvelopes),
    [selectedEnvelopes]
  );

  useEffect(() => {
    return () => {
      dispatch(setSearchContext([]));
    };
  }, []);

  useEffect(() => {
    setMassAssignedLabels(selectedEnvelopeLabels);

    if (!selectedEnvelopes.length) setIsMassLabelPickerOpen(false);
  }, [selectedEnvelopeLabels, selectedEnvelopes.length]);

  useEffect(() => {
    UserFiltersService.getUserFilters()
      .then((response) => setUserFilters(response.data))
      .catch(displayErrorNotification);
  }, []);

  useEffect(() => {
    dispatch(setCurrentUserFilterOguid(currentUserFilter?.oguid ?? null));
  }, [currentUserFilter?.oguid, dispatch]);

  useEffect(() => {
    updateFiltersInState(filtersConfig.data).catch(handleCatchError);
  }, [activeSearchFilterKeysHash, filtersConfig, visibleFilterKeys]);

  useEffect(() => {
    updateFiltersInState(filtersConfig.data).catch(handleCatchError);
    setQueryLikeKey(!queryLikeKey);
    setIsOpenFilters(false);
    setIsFavoritePopoverOpen(false);
  }, [filtersConfig.data, location.search]);

  useEffect(() => {
    setIsOpenFilters(false);
    setIsFavoritePopoverOpen(false);
  }, [directionOrFolder]);

  useEffect(() => {
    if (!isFavoritePopoverOpen) return;

    setFavoriteTitle(currentUserFilter?.title ?? '');
    setFavoriteTitleError('');
  }, [currentUserFilter, isFavoritePopoverOpen]);

  useEffect(() => {
    if (!isFavoritePopoverOpen) return;

    const handleOutsideClick = (evt: MouseEvent | TouchEvent): void => {
      const target = evt.target;

      if (!(target instanceof Node)) return;
      if (favoriteFilterRef.current?.contains(target)) return;

      setIsFavoritePopoverOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isFavoritePopoverOpen]);

  useEffect(() => {
    if (isOpenFilters || !highlightedFilterName) return;

    setHighlightedFilterName(null);
  }, [highlightedFilterName, isOpenFilters]);

  useEffect(() => {
    if (!isOpenFilters || !highlightedFilterName) return;

    const highlightedIndex = filtersOrder.indexOf(highlightedFilterName);
    const filterRows = filtersWrapperRef.current?.querySelectorAll<HTMLLIElement>('.fl-filter');
    const highlightedRow = highlightedIndex >= 0 ? filterRows?.[highlightedIndex] : undefined;

    if (!highlightedRow) return;

    highlightedRow.classList.add(styles.filterHighlight);
    highlightedRow.scrollIntoView({ block: 'center' });
    const removeHighlightTimeout = window.setTimeout(() => {
      highlightedRow.classList.remove(styles.filterHighlight);
    }, 650);

    return () => {
      window.clearTimeout(removeHighlightTimeout);
      highlightedRow.classList.remove(styles.filterHighlight);
    };
  }, [filtersOrder, highlightedFilterName, isOpenFilters]);

  useEffect(() => {
    if (!isOpenFilters) return;

    const handleOutsideClick = (evt: MouseEvent | TouchEvent): void => {
      const target = evt.target;

      if (!(target instanceof Node)) return;
      if (filtersWrapperRef.current?.contains(target)) return;

      changeFiltersState(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpenFilters, visibleFilterKeys]);

  useEffect(() => {
    if (!isOpenFilters) {
      setIsFiltersCloseVisible(false);

      const hideSettingsPlaceholderTimeout = window.setTimeout(() => {
        setIsFiltersPanelTransitionActive(false);
      }, FILTER_PANEL_CLOSE_ANIMATION_MS);

      return () => {
        window.clearTimeout(hideSettingsPlaceholderTimeout);
      };
    }

    setIsFiltersPanelTransitionActive(true);

    const showCloseTimeout = window.setTimeout(() => {
      setIsFiltersCloseVisible(true);
    }, FILTER_PANEL_OPEN_ANIMATION_MS);

    return () => {
      window.clearTimeout(showCloseTimeout);
    };
  }, [isOpenFilters]);

  useEffect(() => {
    if (!isOpenFilters) return;

    const updateFilterMenuPlacement = (target: EventTarget | null): void => {
      if (!(target instanceof Element)) return;

      const placementTarget = target.closest<HTMLElement>(FILTER_PANEL_PLACEMENT_TARGET_SELECTOR);
      const filterRow = placementTarget?.closest<HTMLLIElement>('.fl-filter');

      if (!placementTarget || !filterRow) return;

      const placementTargetRect = placementTarget.getBoundingClientRect();
      const isBelowViewportMiddle =
        placementTargetRect.top + placementTargetRect.height / 2 > window.innerHeight / 2;

      filterRow.classList.toggle(styles.filterMenuPlacementTop, isBelowViewportMiddle);
      filterRow.classList.toggle(styles.filterMenuPlacementBottom, !isBelowViewportMiddle);
    };

    const handlePlacementEvent = (evt: Event): void => {
      updateFilterMenuPlacement(evt.target);
    };

    document.addEventListener('focusin', handlePlacementEvent);
    document.addEventListener('mousedown', handlePlacementEvent);
    document.addEventListener('touchstart', handlePlacementEvent);

    return () => {
      document.removeEventListener('focusin', handlePlacementEvent);
      document.removeEventListener('mousedown', handlePlacementEvent);
      document.removeEventListener('touchstart', handlePlacementEvent);
    };
  }, [isOpenFilters]);

  useEffect(() => {
    if (!isOpenFilters) return;

    const updateFilterRows = (): void => {
      const filterRows = filtersWrapperRef.current?.querySelectorAll<HTMLLIElement>('.fl-filter');
      const systemFilterOrder = envelopesFilters.order as readonly string[];

      filterRows?.forEach((filterRow, index) => {
        const filterName = filtersOrder[index];
        const field = envelopeFieldsByKey[filterName];

        filterRow.classList.toggle(
          styles.customFiltersSeparator,
          Boolean(
            filterName &&
            !systemFilterOrder.includes(filterName) &&
            (index === 0 || systemFilterOrder.includes(filtersOrder[index - 1]))
          )
        );

        if (field?.type !== EnvelopeFieldType.SELECT) return;

        const suggestText = getEnvelopeSelectSearchPlaceholder(field, t);
        const input = filterRow.querySelector<HTMLInputElement>(
          'input[name="suggest-handbook-value"], input[name="multi-suggest-handbook-value"]'
        );

        if (input) {
          input.placeholder = suggestText;
        }

        const hint = filterRow.querySelector<HTMLParagraphElement>(
          '.fl-suggest_handbook_value-options > p:first-child'
        );

        if (hint && hint.textContent !== suggestText) {
          hint.textContent = suggestText;
        }
      });
    };

    const observer = new MutationObserver(updateFilterRows);

    updateFilterRows();

    if (filtersWrapperRef.current) {
      observer.observe(filtersWrapperRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [envelopeFieldsByKey, filters, filtersOrder, isOpenFilters, t]);

  const changeFiltersState = (isOpenFilters: boolean): void => {
    if (!isOpenFilters && !isSubmittingFiltersRef.current) {
      setFiltersOrder(normalizeFiltersOrder(visibleFilterKeys));
    }

    if (!isOpenFilters) {
      setIsFiltersCloseVisible(false);
    } else {
      setIsFiltersPanelTransitionActive(true);
    }

    isSubmittingFiltersRef.current = false;
    setIsOpenFilters(isOpenFilters);
  };

  const handleFilterButtonClick = async (): Promise<void> => {
    // await updateFiltersInState();

    changeFiltersState(!isOpenFilters);
  };

  const handleDownloadRegistryButtonClick = async (): Promise<void> => {
    if (blockRegistryDownload) return;

    const targets = getEnvelopeExportTargets({
      currentDirection,
      isAllEnvelopesSelected,
      isMyTasks: isActionRequiredFolder,
      selectedEnvelopes,
    });

    if (!targets.length) return;

    const filterParams: IParams = {
      ...applyExtendedDateStringFilterParams(searchParams, true),
      ...(isActionRequiredFolder ? { isActionRequired: true } : {}),
    };
    const formatLocale = profileLocale || getSupportedLocale(language);
    const errors: string[] = [];

    const getTerminalJobError = (job: IEnvelopeExcelExportJobResponse): string => {
      if (job.errorDescription) return job.errorDescription;
      if (job.status === EnvelopeExcelExportJobStatus.EXPIRED) {
        return t('envelopes:massDownload.expired');
      }
      if (job.status === EnvelopeExcelExportJobStatus.STOPPED) {
        return t('envelopes:massDownload.stopped');
      }

      return t('common:error');
    };

    const saveBlob = (blob: Blob, fallbackFileName: string, contentDisposition?: string): void => {
      const fileName = getContentDispositionFileName(contentDisposition) ?? fallbackFileName;
      const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

      link.href = url;
      link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const downloadTarget = async (target: IEnvelopeExportTarget): Promise<void> => {
      const params: IParams = {
        ...filterParams,
        boxOguid: currentBoxOguid,
        direction: target.direction,
      };
      let request: IEnvelopeExcelExportJobPostRequest = target.envelopeOguids
        ? { envelopeOguids: target.envelopeOguids }
        : {};
      let response = await EnvelopesService.postExportExcelJob(request, params, formatLocale);
      let part = 1;
      let hasNext = true;

      while (hasNext) {
        const job = await waitForEnvelopeExcelExportJob(
          response.data,
          async (jobOguid) => (await EnvelopesService.getExportExcelJob(jobOguid)).data
        );

        if (job.status !== EnvelopeExcelExportJobStatus.COMPLETED) {
          throw new Error(getTerminalJobError(job));
      }

        const fileResponse = await EnvelopesService.getExportExcelJobFile(job.oguid);
        const contentDisposition = getContentDispositionHeader(fileResponse.headers);
        const directionName = target.direction.toLowerCase();
        const fallbackFileName = `envelopes-${directionName}${part > 1 ? `-${part}` : ''}.xlsx`;

        saveBlob(fileResponse.data, fallbackFileName, contentDisposition);

        hasNext = job.hasNext === true;
        if (!hasNext) continue;

        request = { previousJobOguid: job.oguid };
        response = await EnvelopesService.postExportExcelJob(request, {}, formatLocale);
        part += 1;
      }
    };

    try {
      setBlockRegistryDownload(true);
      for (const target of targets) {
        try {
          await downloadTarget(target);
    } catch (err: any) {
          const error = err.response?.data?.message || err.message;

          errors.push(error === 'EXCEL_EXPORT_TIMEOUT'
            ? t('envelopes:massDownload.timeout')
            : error || t('common:error'));
    }
      }

      if (errors.length) {
        displayErrorNotification({
          response: { data: { message: errors.join('\n') } },
        } as AxiosError);
      }
    } finally {
    setBlockRegistryDownload(false);
    }
  };

  const submitFilters = (filters: ILibFilters): void => {
    const nextOrder = normalizeFiltersOrder(filtersOrder);
    const visibleFilterNames = new Set(nextOrder);
    const nextFiltersViewSettings = {
      ...filterVisibilitySettings,
      hidden: filterVisibilitySettings.order.filter((filterName) =>
        !visibleFilterNames.has(filterName)
      ),
    };

    saveFiltersOrder(nextOrder);
    saveFiltersViewSettings(nextFiltersViewSettings);
    isSubmittingFiltersRef.current = true;

    const paramsFromFilters = getParamsFromFilters(
      filters,
      LS_ENVELOPES_FILTERS_KEY,
      getEnvelopeDateOnlyFilterNames(envelopeFields)
    );

    let params = queryString.stringify(paramsFromFilters);
    const paramsFromSearch = getParamsFromSearch(location.search);
    if (FilterName.QUERY_LIKE in paramsFromSearch) {
      params += `&${FilterName.QUERY_LIKE}=` + paramsFromSearch[FilterName.QUERY_LIKE];
    }
    navigate(`${location.pathname}?${params}`, { replace: true });
    const newFilters = fillRenderValue({ data: filters, search: params, t });

    setFilters(newFilters);
  };

  const getFilters = async (
    filtersData?: ILibFilters
  ): Promise<IFiltersResult> => {
    // const {
    //   fields,
    //   location: { search },
    //   routeAdditionalProp,
    //   state,
    //   t
    // } = this.props
    const { search } = location;

    const myFilters = filtersData ?? filters;
    const data: ILibFilters = { ...myFilters };

    const initialFiltersOrder = normalizeFiltersOrder([
      ...visibleFilterKeys,
      ...activeSearchFilterKeys,
    ]);

    // const isFilter = routeAdditionalProp === RouteName.FILTERS

    // let filtersSearch = search

    // if (!search && isFilter) {
    //   const userFilter = this.getUserFilter()
    //   if (userFilter) filtersSearch = userFilter.params
    // }

    const newFilters = await fillFilters({
      data,
      order: [...initialFiltersOrder],
      // search: filtersSearch,
      search,
      t,
      state: appState,
      // fields,
      key: LS_ENVELOPES_FILTERS_KEY,
      // onSetBreak: this.handleSetBreak,
      user: userOguid,
    });

    const filtersFromStorage = normalizeFiltersOrder(initialFiltersOrder);

    saveFiltersOrder(filtersFromStorage);

    return {
      filters: newFilters,
      order: filtersFromStorage,
    };
  };

  const updateFiltersInState = async (
    filtersData?: ILibFilters
  ): Promise<void> => {
    const { filters, order } = await getFilters(filtersData);

    setFilters(filters);
    setFiltersOrder(order);
  };

  const saveFiltersViewSettings = (
    nextFiltersViewSettings: typeof filterVisibilitySettings
  ): void => {
    const nextEnvelopeViewSettings = getEnvelopeFiltersViewSettingsNextValue(
      userSettings.envelopeViewSettings,
      currentBoxOguid,
      nextFiltersViewSettings,
      currentUserFilter?.oguid
    );

    dispatch(setUserSettings({
      envelopeViewSettings: nextEnvelopeViewSettings,
    }));

    UsersService.updateUserSettings({
      ...userSettings,
      envelopeViewSettings: nextEnvelopeViewSettings,
    }).catch((err) => {
      dispatch(setUserSettings({
        envelopeViewSettings: userSettings.envelopeViewSettings,
      }));
      displayErrorNotification(err);
    });
  };

  const handleAddFilter = (filterName: string): void => {
    const nextOrder = normalizeFiltersOrder([
      ...filtersOrder,
      filterName,
    ]);

    setHighlightedFilterName(filterName);
    setFiltersOrder(nextOrder);
  };

  const handleDeleteFilter = (filterName: string): void => {
    const nextOrder = normalizeFiltersOrder(
      filtersOrder.filter((storedFilterName) => storedFilterName !== filterName)
    );

    setFiltersOrder(nextOrder);
  };

  const handleCatchError = (err: AxiosError): void => {
    dispatch(closeLoader());
    // this.setState({ userFilterIsBreak: true })
    displayErrorNotification(err);
  };

  const handleMassActionClick = (action: EnvelopeAllovedActions): void => {
    setSelectedMassAction(action);
    dispatch(setModal(Modal.ENVELOPE_MASS_ACTION));
  };

  const handleMassDownloadClick = (): void => {
    dispatch(setModal(Modal.ENVELOPE_MASS_DOWNLOAD));
  };

  const handleMassDropdownAction = (action: string): void => {
    if (action === EnvelopeMassUtilityAction.ASSIGN_LABEL) {
      setIsMassLabelPickerOpen((isOpen) => !isOpen);
      return;
    }

    if (action === EnvelopeMassUtilityAction.DOWNLOAD) {
      handleMassDownloadClick();
      return;
    }

    if (action === EnvelopeMassUtilityAction.DOWNLOAD_REGISTRY) {
      void handleDownloadRegistryButtonClick();
      return;
    }

    const envelopeAction = action as EnvelopeAllovedActions;

    if (!massActionAvailability[envelopeAction]) {
      displayBasicNotification({
        content: t('envelopes:massActions.noAvailableForAction', {
          action: t(getMassActionTranslation(envelopeAction)),
        }),
        title: t('envelopes:massActions.messageTitle'),
        type: 'warning',
      });
      return;
    }

    handleMassActionClick(envelopeAction);
  };

  const handleMassLabelAssign = (labelFullKey: string): void => {
    const envelopeOguids = selectedEnvelopes.map(({ oguid }) => oguid);
    if (!labelFullKey || !envelopeOguids.length || updatingMassLabels) return;

    setUpdatingMassLabels(true);
    BoxesService.assignEnvelopeLabel(currentBoxOguid, labelFullKey, envelopeOguids)
      .then(() => {
        const addedLabel = boxLabels.find(({ fullKey }) => fullKey === labelFullKey);

        if (addedLabel) {
          setMassAssignedLabels((current) => {
            const next = addedLabel.group
              ? current.filter(({ group }) => group?.key !== addedLabel.group?.key)
              : [...current];

            if (!next.some(({ fullKey }) => fullKey === addedLabel.fullKey)) next.push(addedLabel);

            return next;
          });
        }

        loadEnvelopes();
      })
      .catch(displayErrorNotification)
      .finally(() => setUpdatingMassLabels(false));
  };

  const handleMassLabelRemove = (labelFullKey: string): void => {
    const envelopeOguids = selectedEnvelopes.map(({ oguid }) => oguid);
    if (!labelFullKey || !envelopeOguids.length || updatingMassLabels) return;

    setUpdatingMassLabels(true);
    BoxesService.removeEnvelopeLabel(currentBoxOguid, labelFullKey, envelopeOguids)
      .then(() => {
        setMassAssignedLabels((current) => current.filter(({ fullKey }) => fullKey !== labelFullKey));
        loadEnvelopes();
      })
      .catch(displayErrorNotification)
      .finally(() => setUpdatingMassLabels(false));
  };

  const handleMassActionClose = (reload?: boolean): void => {
    setSelectedMassAction(null);

    if (reload) {
      loadEnvelopes();
    }
  };

  const validateFavoriteTitle = (title: string): boolean => {
    const trimmedTitle = title.trim();

    if (!trimmedTitle) {
      setFavoriteTitleError(t('filters:favoriteFilter.nameRequired'));

      return false;
    }

    if (trimmedTitle.length > USER_FILTER_TITLE_MAX_LENGTH) {
      setFavoriteTitleError(
        t('filters:favoriteFilter.nameMaxLength', {
          count: USER_FILTER_TITLE_MAX_LENGTH,
        })
      );

      return false;
    }

    setFavoriteTitleError('');

    return true;
  };

  const handleFavoriteButtonClick = (): void => {
    if (!hasFavoriteFilterParams) return;

    setFavoriteTitle(currentUserFilter?.title ?? '');
    setFavoriteTitleError('');
    setIsFavoritePopoverOpen((isOpen) => !isOpen);
  };

  const handleFavoriteTitleChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
    setFavoriteTitle(evt.target.value);

    if (favoriteTitleError) {
      setFavoriteTitleError('');
    }
  };

  const handleFavoriteSubmit = (evt: FormEvent<HTMLFormElement>): void => {
    evt.preventDefault();

    if (!favoriteFilterRoute || !favoriteFilterParams || isFavoriteSaving) return;
    if (!validateFavoriteTitle(favoriteTitle)) return;

    const payload = {
      params: favoriteFilterParams,
      route: favoriteFilterRoute,
      title: favoriteTitle.trim(),
    };
    const saveRequest = currentUserFilter
      ? UserFiltersService.putUserFilter(currentUserFilter.oguid, payload)
      : UserFiltersService.postUserFilter(payload);

    setIsFavoriteSaving(true);

    saveRequest
      .then((response) => {
        if (!currentUserFilter) {
          const nextEnvelopeViewSettings = getEnvelopeFavoriteViewSettingsNextValue(
            userSettings.envelopeViewSettings,
            response.data.oguid,
            currentBoxOguid
          );

          dispatch(setUserSettings({
            envelopeViewSettings: nextEnvelopeViewSettings,
          }));
          UsersService.updateUserSettings({
            ...userSettings,
            envelopeViewSettings: nextEnvelopeViewSettings,
          }).catch((err) => {
            dispatch(setUserSettings({
              envelopeViewSettings: userSettings.envelopeViewSettings,
            }));
            displayErrorNotification(err);
          });
        }

        setUserFilters((currentFilters) => currentUserFilter
          ? currentFilters.map((filter) =>
            filter.oguid === currentUserFilter.oguid ? response.data : filter
          )
          : [...currentFilters, response.data]
        );
        notifyUserFiltersUpdated();
        setIsFavoritePopoverOpen(false);
        displaySuccessNotification({
          title: t(currentUserFilter ? 'common:saved' : 'common:created'),
        });
      })
      .catch(displayErrorNotification)
      .finally(() => setIsFavoriteSaving(false));
  };

  const handleFavoriteDelete = (): void => {
    if (!currentUserFilter || isFavoriteSaving) return;

    displayConfirmNotification({
      content: t('filters:favoriteFilter.deleteConfirm', {
        name: currentUserFilter.title,
      }),
      onSubmit: () => {
        setIsFavoriteSaving(true);

        UserFiltersService.deleteUserFilter(currentUserFilter.oguid)
          .then(() => {
            const nextEnvelopeViewSettings = getEnvelopeFavoriteViewSettingsRemovedValue(
              userSettings.envelopeViewSettings,
              currentUserFilter.oguid
            );

            if (nextEnvelopeViewSettings !== userSettings.envelopeViewSettings) {
              dispatch(setUserSettings({
                envelopeViewSettings: nextEnvelopeViewSettings,
              }));
              UsersService.updateUserSettings({
                ...userSettings,
                envelopeViewSettings: nextEnvelopeViewSettings,
              }).catch(displayErrorNotification);
            }

            setUserFilters((currentFilters) =>
              currentFilters.filter((filter) => filter.oguid !== currentUserFilter.oguid)
            );
            notifyUserFiltersUpdated();
            setIsFavoritePopoverOpen(false);
            displaySuccessNotification({ title: t('common:deleted') });
          })
          .catch(displayErrorNotification)
          .finally(() => setIsFavoriteSaving(false));
      },
    });
  };

  const renderFavoriteFilter = (): React.ReactNode => {
    if (!hasFavoriteFilterParams) return null;

    const isFavorite = Boolean(currentUserFilter);

    return (
      <div className={styles.favoriteFilter} ref={favoriteFilterRef}>
        <button
          aria-label={t('filters:favoriteFilter.title')}
          className={cn(styles.favoriteButton, isFavorite && styles.favoriteButtonActive)}
          onClick={handleFavoriteButtonClick}
          title={t('filters:favoriteFilter.title')}
          type='button'
        >
          <StarIcon isActive={isFavorite} />
        </button>
        {isFavoritePopoverOpen && (
          <form className={styles.favoritePopover} onSubmit={handleFavoriteSubmit}>
            <label className={styles.favoriteLabel} htmlFor='favorite-filter-title'>
              {t('filters:favoriteFilter.name')}
            </label>
            <input
              autoFocus
              autoComplete='off'
              className={cn(styles.favoriteInput, favoriteTitleError && styles.favoriteInputInvalid)}
              disabled={isFavoriteSaving}
              id='favorite-filter-title'
              maxLength={USER_FILTER_TITLE_MAX_LENGTH}
              onChange={handleFavoriteTitleChange}
              placeholder={t('filters:favoriteFilter.namePlaceholder')}
              spellCheck={false}
              value={favoriteTitle}
            />
            {favoriteTitleError && (
              <div className={styles.favoriteError}>{favoriteTitleError}</div>
            )}
            <div className={styles.favoriteActions}>
              {currentUserFilter && (
                <button
                  className={cn(styles.favoriteActionButton, styles.favoriteDeleteButton)}
                  disabled={isFavoriteSaving}
                  onClick={handleFavoriteDelete}
                  type='button'
                >
                  {t('common:delete')}
                </button>
              )}
              <button
                className={cn(styles.favoriteActionButton, styles.favoriteSaveButton)}
                disabled={isFavoriteSaving}
                type='submit'
              >
                {t(currentUserFilter ? 'common:save' : 'common:add')}
              </button>
            </div>
          </form>
        )}
      </div>
    );
  };

  const renderMassActions = (): React.ReactNode => {
    if (!selectedEnvelopes.length) return null;

    const inlineMassActionStatus = [
      blockRegistryDownload ? t('envelopes:massActions.exportingExcel') : '',
      updatingMassLabels ? t('envelopes:massActions.updatingLabels') : '',
    ].filter(Boolean).join(', ');
    const labelControls = isMassLabelPickerOpen ? (
      <div
        className={cn(styles.massLabelPicker, styles.massLabelPickerInline)}
      >
        <div className={styles.massLabelPickerSection}>
          <p>{t('envelope:assignedLabels')}</p>
          <div className={styles.massAssignedLabels}>
            {massAssignedLabels.map((label) => (
              <span className={styles.massAssignedLabel} key={label.fullKey}>
                <span
                  className={styles.massLabelBadge}
                  style={{
                    backgroundColor: label.color ?? DEFAULT_LABEL_COLOR,
                    color: getReadableTextColor(label.color ?? DEFAULT_LABEL_COLOR),
                  }}
                >
                  {label.name}
                </span>
                <button
                  aria-label={`${t('common:delete')} ${label.name}`}
                  className={styles.massLabelRemove}
                  disabled={updatingMassLabels}
                  onClick={() => handleMassLabelRemove(label.fullKey)}
                  type='button'
                >
                  ×
                </button>
              </span>
            ))}
            {!massAssignedLabels.length && (
              <small>{t('envelope:noAssignedLabels')}</small>
            )}
          </div>
        </div>
        <div className={styles.massLabelPickerSection}>
          <label htmlFor='mass-assign-label'>{t('envelope:addLabel')}</label>
          <select
            disabled={updatingMassLabels}
            id='mass-assign-label'
            onChange={(evt) => handleMassLabelAssign(evt.currentTarget.value)}
            value=''
          >
            <option value=''>{t('envelope:selectLabel')}</option>
            {sortedBoxLabels.map((label) => (
              <option key={label.fullKey} value={label.fullKey}>
                {label.group ? `${label.group.name} / ${label.name}` : label.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    ) : null;
    const actionGroups: IMassiveActionDropdownItem[][] = [
      ENVELOPE_MASS_ACTIONS.map((action) => ({
        name: action,
        translation: t(getMassActionTranslation(action)),
      })),
      [
        {
          name: EnvelopeMassUtilityAction.ASSIGN_LABEL,
          translation: t('envelope:assignLabel'),
          isDisabled: updatingMassLabels,
          keepOpen: true,
          expandedContent: labelControls,
        },
        {
          name: EnvelopeMassUtilityAction.DOWNLOAD,
          translation: t('envelopes:massDownload.download'),
        },
        {
          name: EnvelopeMassUtilityAction.DOWNLOAD_REGISTRY,
          translation: t('common:downloadRegistry'),
          isDisabled: blockRegistryDownload,
        },
      ],
    ];

    return (
      <div className={styles.massActions}>
        <span className='text-muted mr-3'>
          {t('envelopes:massActions:selectedCount', { count: selectedEnvelopes.length })}
        </span>
        <div className={cn(styles.massActionControls, 'mr-4')}>
          <MassiveActionsDropdown
            actionGroups={actionGroups}
            onActionSelect={handleMassDropdownAction}
            onPopoverClose={() => setIsMassLabelPickerOpen(false)}
          />
          {inlineMassActionStatus && (
            <div className={styles.massActionProgress} role='status'>
              <div className={styles.massActionSpinner}>
                <Spinner />
              </div>
              <span className={styles.massActionStatus}>{inlineMassActionStatus}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSettingsButton = (): React.ReactNode => (
    <Button
      classes={styles.settingsIconButton}
      minWidth={false}
      onClick={onOpenFieldViewSettings}
      title={t('boxes:fieldView.settings')}
    >
      <IconCog />
    </Button>
  );

  return (
    <>
      <div className='d-flex align-items-center mb-4'>
        <Refresh onClick={loadEnvelopes} title={t('common:refresh')} />
        {renderMassActions()}
        <EnvelopeMassDownloadModal
          boxOguid={currentBoxOguid}
          currentDirection={currentDirection}
          envelopeFields={envelopeFields}
          filterParams={zipExportFilterParams}
          formatLocale={profileLocale || getSupportedLocale(language)}
          isAllEnvelopesSelected={isAllEnvelopesSelected}
          isMyTasks={isActionRequiredFolder}
          selectedEnvelopes={selectedEnvelopes}
        />

        <QueryLikeFilter
          className='ml-auto mr-4'
          key={queryLikeKey + ''}
          placeholder={t('envelopes:searchPlaceholder')}
          // userFilter={userFilter}
        />
        <Button onClick={handleFilterButtonClick}>
          {t('common:filters')}
        </Button>
        {isFieldViewSettingsOpen || isFiltersPanelTransitionActive ? (
          <div className={styles.settingsButtonPlaceholder} aria-hidden='true' />
        ) : (
          renderSettingsButton()
        )}
      </div>
      <div
        className={cn(styles.filtersPanel, 'd-flex align-items-start')}
        ref={filtersWrapperRef}
      >
        {renderFavoriteFilter()}
        {isFiltersCloseVisible && (
          <CloseButton
            classes={styles.filtersPanelClose}
            onClick={() => changeFiltersState(false)}
            text={t('common:close')}
          />
        )}
        <Filters
          key={`${currentBoxOguid}-${directionOrFolder ?? currentDirection ?? ''}-${location.search}`}
          className='mb-4'
          filters={filters}
          language={language}
          submitFilters={submitFilters}
          translations={filtersTranslations}
          isOpen={isOpenFilters}
          changeOpenStatus={changeFiltersState}
          order={filtersOrder}
          options={inactiveFieldFilterOptions}
          onAdd={handleAddFilter}
          onDelete={handleDeleteFilter}
          // isSaved={this.isUserFilterSaved()}
          // onSave={this.handleOpenUserFiltersModal}
          // isHorizontalNavMode={getIsHorizontalNavMode()}
          // hasResult={hasFilter}
          instance={getOldInstance(getInstance(instance))}
        />
      </div>
      {selectedMassAction && (
        <EnvelopeMassActionModal
          action={selectedMassAction}
          selectedEnvelopes={selectedEnvelopes}
          onCloseModal={handleMassActionClose}
          onTasksUpdated={loadEnvelopes}
        />
      )}
    </>
  );
};

export default EnvelopesHead;
